import React, { memo,useEffect, useRef, useState } from 'react';
import RefreshIcon from '@mui/icons-material/Refresh';
import { Box, IconButton, Tooltip } from '@mui/material';
import { keyframes, styled } from '@mui/system';

export type AutosaveState = 'idle' | 'saving' | 'saved' | 'error';

export interface AutosaveStatusProps {
  /** Current autosave state */
  status: AutosaveState;
  /** Whether there are unsaved changes */
  isDirty: boolean;
  /** Whether the form/content is empty */
  isEmpty?: boolean;
  /** Last saved timestamp */
  lastSavedAt?: string | Date | null;
  /** Callback for retry action */
  onRetry?: () => void;
  /** Custom messages for each state */
  messages?: {
    unsaved?: string;
    saving?: string;
    saved?: string;
    error?: string;
  };
  /** Show text alongside the status dot */
  verbose?: boolean;
}

const SAVED_DISPLAY_DURATION = 2000; // Reduced to 2 seconds for subtler experience

interface DotProps {
  color: string;
  animate: boolean;
  animationType?: 'pulse' | 'gentle';
  tooltip: string;
}

// Pulse animation for saving state
const pulse = keyframes`
  0% {
    opacity: 0.4;
    transform: scale(1);
  }
  50% {
    opacity: 1;
    transform: scale(1.1);
  }
  100% {
    opacity: 0.4;
    transform: scale(1);
  }
`;

// Gentler pulse for pending changes
const gentlePulse = keyframes`
  0% {
    opacity: 0.6;
  }
  50% {
    opacity: 1;
  }
  100% {
    opacity: 0.6;
  }
`;

// Status dot component with memoization for performance
const StatusDotBase = styled(Box, {
  shouldForwardProp: prop => prop !== 'dotColor' && prop !== 'animate' && prop !== 'animationType',
})<{ dotColor: string; animate?: boolean; animationType?: 'pulse' | 'gentle' }>(
  ({ dotColor, animate, animationType = 'pulse' }) => ({
    width: 8,
    height: 8,
    borderRadius: '50%',
    backgroundColor: dotColor,
    transition: 'background-color 400ms cubic-bezier(0.4, 0, 0.2, 1), transform 200ms ease-in-out',
    animation: animate
      ? animationType === 'pulse'
        ? `${pulse} 1.5s ease-in-out infinite`
        : `${gentlePulse} 2s ease-in-out infinite`
      : 'none',
  }),
);

// Memoized to prevent re-renders when parent updates
const StatusDot = memo(StatusDotBase);

export const AutosaveStatus: React.FC<AutosaveStatusProps> = ({
  status,
  isDirty,
  isEmpty: _isEmpty = false,
  lastSavedAt,
  onRetry,
  messages = {},
  verbose = false,
}) => {
  const [showSaved, setShowSaved] = useState(false);
  const [, forceUpdate] = useState({});

  // Default messages for tooltips
  const defaultMessages = {
    unsaved: 'Unsaved changes',
    saving: 'Saving...',
    saved: 'All changes saved',
    error: 'Failed to save',
  };

  const msg = { ...defaultMessages, ...messages };

  // Track if we've completed at least one save during this session
  const hasSavedInSessionRef = useRef(false);

  // Mark that we've interacted when form becomes dirty or starts saving
  useEffect(() => {
    if (status === 'saving' || isDirty) {
      hasSavedInSessionRef.current = true;
    }
  }, [status, isDirty]);

  // Show saved indicator only after saves in current session
  useEffect(() => {
    // Only show saved if we've actually saved during this session
    if (hasSavedInSessionRef.current && lastSavedAt && status === 'idle' && !isDirty) {
      setShowSaved(true);
      const timer = setTimeout(() => {
        setShowSaved(false);
      }, SAVED_DISPLAY_DURATION);
      return () => {
        clearTimeout(timer);
      };
    }
  }, [lastSavedAt, status, isDirty]);

  // Auto-update relative time display
  useEffect(() => {
    if (!verbose || !lastSavedAt || isDirty || status !== 'idle') return;

    // Calculate smart update intervals based on elapsed time
    const getUpdateInterval = () => {
      const saved = new Date(lastSavedAt);
      const now = new Date();
      const seconds = Math.floor((now.getTime() - saved.getTime()) / 1000);

      if (seconds < 60) return 10000; // Update every 10s for first minute
      if (seconds < 3600) return 60000; // Update every minute for first hour
      if (seconds < 86400) return 300000; // Update every 5 minutes for first day
      return null; // Don't update after a day
    };

    const interval = getUpdateInterval();
    if (!interval) return;

    const timer = setInterval(() => {
      forceUpdate({});
    }, interval);

    return () => {
      clearInterval(timer);
    };
  }, [lastSavedAt, verbose, isDirty, status]);

  // Format relative time for tooltip
  const formatRelativeTime = (date: string | Date) => {
    const saved = new Date(date);
    const now = new Date();
    const seconds = Math.floor((now.getTime() - saved.getTime()) / 1000);

    if (seconds < 60) return 'just now';
    if (seconds < 3600) return `${Math.floor(seconds / 60)} min ago`;
    if (seconds < 86400) return `${Math.floor(seconds / 3600)} hours ago`;
    return saved.toLocaleDateString();
  };

  // Get dot color and animation state
  const getDotProps = (): DotProps | null => {
    // Error state takes priority
    if (status === 'error') {
      return { color: '#ef4444', animate: false, tooltip: msg.error };
    }

    // Show saving indicator when dirty (even during debounce) or actively saving
    if (isDirty || status === 'saving') {
      // Use stronger pulse when actually saving, gentler when just dirty
      const animationType: 'pulse' | 'gentle' = status === 'saving' ? 'pulse' : 'gentle';
      const tooltip = status === 'saving' ? msg.saving : 'Changes will be saved automatically';
      return { color: '#f59e0b', animate: true, animationType, tooltip }; // Amber-500
    }

    // Show saved state briefly after save completes
    if (showSaved) {
      const timeAgo = lastSavedAt ? ` ${formatRelativeTime(lastSavedAt)}` : '';
      return { color: '#10b981', animate: false, tooltip: `${msg.saved}${timeAgo}` };
    }

    // In verbose mode, show gray dot when idle after interaction
    if (verbose && hasSavedInSessionRef.current) {
      return { color: '#9ca3af', animate: false, tooltip: 'No unsaved changes' }; // Gray-400
    }

    return null;
  };

  const dotProps = getDotProps();
  const isVisible = !!dotProps || (verbose && hasSavedInSessionRef.current);

  // Get status text for verbose mode
  const getStatusText = () => {
    if (!verbose || !hasSavedInSessionRef.current) return null;

    if (status === 'error') return 'Failed';
    if (isDirty || status === 'saving') return 'Saving';
    if (showSaved) return 'Saved';
    if (lastSavedAt) {
      const timeAgo = formatRelativeTime(lastSavedAt);
      // When transitioning from green to gray, show "Saved just now"
      return `Saved ${timeAgo}`;
    }
    return 'All changes saved';
  };

  const statusText = getStatusText();

  // Keep component mounted but control visibility with opacity
  return (
    <Box
      sx={{
        opacity: isVisible ? 1 : 0,
        transform: isVisible ? 'scale(1)' : 'scale(0.8)',
        transition: 'all 300ms cubic-bezier(0.4, 0, 0.2, 1)',
        pointerEvents: isVisible ? 'auto' : 'none',
        display: 'flex',
        alignItems: 'center',
        gap: 1,
      }}
    >
      {/* Status text in verbose mode */}
      {statusText && (
        <Box
          component="span"
          sx={{
            fontSize: '0.75rem',
            color: 'text.secondary',
            opacity: 0.8,
            transition: 'color 300ms ease',
            userSelect: 'none',
          }}
        >
          {statusText}
        </Box>
      )}

      {/* Error state with retry option */}
      {status === 'error' && onRetry ? (
        <Tooltip title={dotProps?.tooltip || ''}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <StatusDot
              dotColor={dotProps?.color || 'transparent'}
              animate={dotProps?.animate || false}
              animationType={dotProps?.animationType}
            />
            <IconButton
              size="small"
              onClick={onRetry}
              sx={{
                p: 0.25,
                opacity: 0.7,
                '&:hover': { opacity: 1 },
              }}
            >
              <RefreshIcon sx={{ fontSize: 14, color: 'error.main' }} />
            </IconButton>
          </Box>
        </Tooltip>
      ) : dotProps ? (
        /* Simple dot indicator with tooltip */
        <Tooltip title={dotProps.tooltip || ''} placement="bottom">
          <StatusDot
            dotColor={dotProps.color || 'transparent'}
            animate={dotProps.animate || false}
            animationType={dotProps.animationType}
            sx={{ cursor: 'default' }}
          />
        </Tooltip>
      ) : null}
    </Box>
  );
};
